import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Mail, Phone, ChevronDown, ChevronUp, Share2 } from 'lucide-react';
import { siteSettingsService } from '../../services/siteSettingsService';

const TikTokIcon = ({ className = 'w-5 h-5', ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className} {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const ClientFooter = () => {
  const currentYear = new Date().getFullYear();
  const [openSections, setOpenSections] = useState({});

  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });
  const settings = settingsResponse?.data || {};

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const footerLinks = {
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'How It Works', href: '/how-it-works' },
      // { name: 'Careers', href: '/careers' },
      // { name: 'Press', href: '/press' },
      { name: 'Blog', href: '/blog' },
    ],
    support: [
      { name: 'Help Center', href: '/help' },
      { name: 'Contact Us', href: '/contact' },
      { name: 'Safety', href: '/safety' },
      // { name: 'Report a Problem', href: '/report' },
    ],
    vendors: [
      { name: 'Become a Vendor', href: '/register' },
      // { name: 'Vendor Resources', href: '/vendor' },
      { name: 'Vendor Success Stories', href: '/vendor/success' },
      // { name: 'Vendor Support', href: '/vendor/support' },
    ],
    legal: [
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Privacy Policy', href: '/privacy' },
      // { name: 'Cookie Policy', href: '/cookies' },
      // { name: 'Accessibility', href: '/accessibility' },
    ],
  };

  const iconMap = { Facebook, Twitter, Instagram, LinkedIn: Linkedin, YouTube: Youtube, TikTok: TikTokIcon, Pinterest: Share2 };
  const getIcon = (name) => iconMap[name] || Share2;

  const hasSettings = !!settingsResponse?.data;
  const socialLinks = hasSettings && settings.social_links?.length > 0
    ? settings.social_links
        .filter((s) => s.platform && s.url)
        .map((s) => ({ name: s.platform, icon: getIcon(s.platform), href: s.url }))
    : hasSettings
      ? [
          { name: 'Facebook', icon: Facebook, href: settings.facebook_url },
          { name: 'Twitter', icon: Twitter, href: settings.twitter_url },
          { name: 'Instagram', icon: Instagram, href: settings.instagram_url },
          { name: 'LinkedIn', icon: Linkedin, href: settings.linkedin_url },
          { name: 'YouTube', icon: Youtube, href: settings.youtube_url },
          { name: 'TikTok', icon: TikTokIcon, href: settings.tiktok_url },
          { name: 'Pinterest', icon: Share2, href: settings.pinterest_url },
        ].filter((s) => s.href)
      : [
          { name: 'Facebook', icon: Facebook, href: 'https://facebook.com' },
          { name: 'Twitter', icon: Twitter, href: 'https://twitter.com' },
          { name: 'Instagram', icon: Instagram, href: 'https://instagram.com' },
          { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com' },
        ];

  return (
    <footer className="bg-black text-white">
      <div className="page-container">
        {/* Main Footer Content */}
        <div className="py-6 md:py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {/* Company Info - Column 1 */}
            <div>
              <Link to="/" className="flex items-center space-x-2 mb-4">
                <img
                  src={settings.site_logo || '/images/VendaLOGO1.png'}
                  alt={settings.site_name || 'Logo'}
                  className="w-28 h-10 md:w-36 md:h-12 object-contain"
                />
              </Link>

              <p className="text-gray-300 mb-4 md:mb-6 max-w-md font-extra-light text-xs md:text-sm hidden md:block">
                {settings.footer_tagline || 'Connecting event organizers with top-rated vendors for photo booths, entertainment, and event services. Making your special moments unforgettable.'}
              </p>

              {/* Contact Info - Mobile: Simplified, Desktop: Full */}
              <div className="space-y-2 md:space-y-3">
                {settings.site_email && (
                  <div className="flex items-center text-gray-300 font-extra-light text-xs md:text-sm">
                    <Mail className="w-3.5 h-3.5 md:w-4 md:h-4 mr-2 md:mr-3" />
                    <span>{settings.site_email}</span>
                  </div>
                )}
                {settings.site_phone && (
                  <div className="flex items-center text-gray-300 font-extra-light text-xs md:text-sm">
                    <Phone className="w-3.5 h-3.5 md:w-4 md:h-4 mr-2 md:mr-3" />
                    <span className="text-xs md:text-sm">{settings.site_phone}</span>
                  </div>
                )}
                {!settings.site_email && !settings.site_phone && (
                  <>
                    <div className="flex items-center text-gray-300 font-extra-light text-xs md:text-sm">
                      <Mail className="w-3.5 h-3.5 md:w-4 md:h-4 mr-2 md:mr-3" />
                      <span>vendalist@gmail.com</span>
                    </div>
                    <div className="flex items-center text-gray-300 font-extra-light text-xs md:text-sm">
                      <Phone className="w-3.5 h-3.5 md:w-4 md:h-4 mr-2 md:mr-3" />
                      <span className="text-xs md:text-sm">1-800-PHOTO-BOOTH</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Company Links - Column 2 */}
            <div className="border-b border-gray-800 md:border-b-0">
              <button
                onClick={() => toggleSection('company')}
                className="w-full md:w-auto flex items-center justify-between md:block py-4 md:py-0"
              >
                <h3 className="text-base md:text-lg font-semibold mb-0 md:mb-4">Company</h3>
                <span className="md:hidden">
                  {openSections.company ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </span>
              </button>
              <ul className={`space-y-2 overflow-hidden transition-all duration-300 ${
                openSections.company ? 'max-h-96 pb-4 md:pb-0' : 'max-h-0 md:max-h-96'
              }`}>
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                      className="text-gray-300 hover:text-white transition-colors font-extra-light text-xs md:text-sm block py-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

            {/* Support Links - Column 3 */}
            <div className="border-b border-gray-800 md:border-b-0">
              <button
                onClick={() => toggleSection('support')}
                className="w-full md:w-auto flex items-center justify-between md:block py-4 md:py-0"
              >
                <h3 className="text-base md:text-lg font-semibold mb-0 md:mb-4">Support</h3>
                <span className="md:hidden">
                  {openSections.support ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </span>
              </button>
              <ul className={`space-y-2 overflow-hidden transition-all duration-300 ${
                openSections.support ? 'max-h-96 pb-4 md:pb-0' : 'max-h-0 md:max-h-96'
              }`}>
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                      className="text-gray-300 hover:text-white transition-colors font-extra-light text-xs md:text-sm block py-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

            {/* Vendor Links - Column 4 */}
            <div className="border-b border-gray-800 md:border-b-0">
              <button
                onClick={() => toggleSection('vendors')}
                className="w-full md:w-auto flex items-center justify-between md:block py-4 md:py-0"
              >
                <h3 className="text-base md:text-lg font-semibold mb-0 md:mb-4">For Vendors</h3>
                <span className="md:hidden">
                  {openSections.vendors ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </span>
              </button>
              <ul className={`space-y-2 overflow-hidden transition-all duration-300 ${
                openSections.vendors ? 'max-h-96 pb-4 md:pb-0' : 'max-h-0 md:max-h-96'
              }`}>
              {footerLinks.vendors.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                      className="text-gray-300 hover:text-white transition-colors font-extra-light text-xs md:text-sm block py-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            </div>
          </div>
        </div>

     

        {/* Bottom Footer */}
        <div className="py-4 md:py-6 border-t border-gray-800">
          {/* Mobile: Stacked Layout */}
          <div className="flex flex-col md:hidden space-y-4">
            {/* Social Links - Mobile */}
            <div className="flex justify-center space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
            
            {/* Copyright - Mobile */}
            <div className="text-gray-400 text-xs text-center">
              {settings.footer_copyright || `© ${currentYear} ${settings.site_name || 'VendaList'}. All rights reserved.`}
            </div>
            
            {/* Legal Links - Mobile: Compact */}
            <div className="flex flex-wrap justify-center gap-3 text-xs">
              {footerLinks.legal.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
          
          {/* Desktop: Horizontal Layout */}
          <div className="hidden md:flex flex-row justify-between items-center">
            <div className="text-gray-400 text-sm">
              {settings.footer_copyright || `© ${currentYear} ${settings.site_name || 'VendaList'}. All rights reserved.`}
            </div>
            
            {/* Social Links - Desktop */}
            <div className="flex space-x-4">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label={social.name}
              >
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>

            {/* Legal Links - Desktop */}
          <div className="flex space-x-6 text-sm">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {link.name}
              </Link>
            ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ClientFooter;
